import {
    createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef,
} from 'react';
import { shopApi } from '../services/shopApi';

const STORAGE_KEY = 'shop_cart_v1';
const MAX_QTY = 99;

const CartContext = createContext(null);

const readStorage = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const clampQty = (qty, stock) => {
    const max = stock > 0 ? Math.min(stock, MAX_QTY) : MAX_QTY;
    return Math.max(1, Math.min(Number(qty) || 1, max));
};

// Берём из товара только то, что нужно корзине
export const toCartItem = (p) => ({
    id: p.id,
    slug: p.slug,
    name: p.name,
    part_number: p.part_number,
    brand: p.brand || null,
    price: Number(p.price),
    image: p.image || null,
    stock: p.stock ?? 0,
});

function reducer(state, action) {
    switch (action.type) {
        case 'add': {
            const { item, qty } = action;
            const found = state.find((i) => i.id === item.id);
            if (found) {
                return state.map((i) =>
                    i.id === item.id ? { ...i, ...item, quantity: clampQty(i.quantity + qty, item.stock) } : i
                );
            }
            return [...state, { ...item, quantity: clampQty(qty, item.stock) }];
        }
        case 'qty':
            return state.map((i) => (i.id === action.id ? { ...i, quantity: clampQty(action.qty, i.stock) } : i));
        case 'remove':
            return state.filter((i) => i.id !== action.id);
        case 'clear':
            return [];
        case 'replace':
            return action.items;
        case 'sync': {
            // action.products — актуальные данные с сервера. Товары, которых нет / нет в наличии, удаляем.
            const fresh = new Map(action.products.map((p) => [p.id, p]));
            return state
                .filter((i) => fresh.has(i.id) && fresh.get(i.id).stock > 0)
                .map((i) => {
                    const f = fresh.get(i.id);
                    return {
                        ...i,
                        name: f.name,
                        price: Number(f.price),
                        stock: f.stock,
                        image: f.image,
                        quantity: clampQty(i.quantity, f.stock),
                    };
                });
        }
        default:
            return state;
    }
}

export function CartProvider({ children }) {
    const [items, dispatch] = useReducer(reducer, undefined, readStorage);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    // Сохраняем корзину
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            /* приватный режим / переполнение — корзина живёт только в памяти */
        }
    }, [items]);

    // Синхронизация между вкладками
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) dispatch({ type: 'replace', items: readStorage() });
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    /**
     * Сверяет корзину с сервером (цены, остатки, доступность).
     * Возвращает true, если что-то изменилось.
     */
    const syncWithServer = useCallback(async () => {
        const current = itemsRef.current;
        if (!current.length) return false;

        const res = await shopApi.checkCart(current.map((i) => ({ part_id: i.id })));
        const products = res.data || [];
        const fresh = new Map(products.map((p) => [p.id, p]));

        const changed = current.some((i) => {
            const f = fresh.get(i.id);
            return !f || f.stock <= 0 || Number(f.price) !== Number(i.price) || i.quantity > f.stock;
        });

        dispatch({ type: 'sync', products });
        return changed;
    }, []);

    const value = useMemo(
        () => ({
            items,
            count: items.reduce((s, i) => s + i.quantity, 0),
            total: items.reduce((s, i) => s + i.price * i.quantity, 0),
            qtyOf: (id) => items.find((i) => i.id === id)?.quantity || 0,
            add: (item, qty = 1) => dispatch({ type: 'add', item, qty }),
            setQty: (id, qty) => dispatch({ type: 'qty', id, qty }),
            remove: (id) => dispatch({ type: 'remove', id }),
            clear: () => dispatch({ type: 'clear' }),
            syncWithServer,
        }),
        [items, syncWithServer]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart нужно использовать внутри <CartProvider> (ShopLayout)');
    return ctx;
};
